import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Save, X, ArrowLeft, Upload, Undo, Redo, FileEdit, FilePlus2, History, Info, MessageSquare } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EditResourceBudgetForm } from './edit-resource-budget-form';
import { ImportBudgetDialog, type ImportPreview } from './import-budget-dialog';
import type { CostCentre } from '@/components/blocks/cost-centres/cost-centres-table';
import type { HierarchicalCostCentre } from '@/components/blocks/cost-centres/cost-centres-hierarchical-table';
import { BudgetExportModal } from './budget-export-modal';
import { BudgetRevisionPanel } from './budget-revision-panel';
import { RevisionCascadeConfirmDialog } from './revision-cascade-confirm-dialog';
import { Download } from 'lucide-react';
import { useBudgetPermissions } from '@/hooks/use-budget-permissions';
import type { BudgetData, BudgetRevision, RevisionCascadeRecord, RevisionReasonCategory } from '@/types/budget.types';
import { fetchRoles, type RoleOption } from '@/lib/data-service';
import { toast } from 'sonner';

interface EditResourceBudgetPageProps {
  projectId: number;
  budgetData: BudgetData;
  projectStartDate?: string;
  projectEndDate?: string;
  /** 'edit' = standard revision save flow | 'what-if' = ephemeral scenario mode */
  mode?: 'edit' | 'what-if';
  /** When set, shows a contextual banner indicating the budget was loaded from this historical revision */
  loadedFromRevision?: import('@/types/budget.types').BudgetRevision;
  /** When true, shows a distinct banner indicating the user is creating a new revision */
  isNewRevision?: boolean;
  onSave: (updatedBudget: Partial<BudgetData>, revisionData?: Partial<BudgetRevision>) => Promise<void>;
  onCancel: () => void;
  /** Called when PM clicks "Make Active" — promotes a draft to the active revision */
  onMakeActive?: () => void;
  /** Cost Centre mode — when true, renders per-cost-centre grouped role rows */
  isCostCentreMode?: boolean;
  /** Flat list of cost centres for Cost Centre mode */
  costCentres?: CostCentre[];
  /** Hierarchical cost centres for Cost Centre mode structure view */
  hierarchicalCostCentres?: HierarchicalCostCentre[];
  /** When true, shows hierarchical/structure view in Cost Centre mode */
  isStructureView?: boolean;
}

export function EditResourceBudgetPage({
  projectId,
  budgetData,
  projectStartDate,
  projectEndDate,
  mode = 'edit',
  loadedFromRevision,
  isNewRevision = false,
  onSave,
  onCancel,
  onMakeActive,
  isCostCentreMode = false,
  costCentres = [],
  hierarchicalCostCentres = [],
  isStructureView = false,
}: EditResourceBudgetPageProps) {
  const currentYear = new Date().getFullYear();
  
  // Calculate years based on project dates
  const years = (() => {
    if (!projectStartDate || !projectEndDate) {
      // Fallback to default range if no project dates
      return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    }
    
    const startYear = new Date(projectStartDate).getFullYear();
    const endYear = new Date(projectEndDate).getFullYear();
    
    // Generate array of years from start to end
    const yearRange: number[] = [];
    for (let year = startYear; year <= endYear; year++) {
      yearRange.push(year);
    }
    
    return yearRange;
  })();
  
  // Default to the current calendar year when it falls within the project range,
  // otherwise fall back to the first project year.
  const initialYear = years.includes(currentYear) ? currentYear : (years[0] ?? currentYear);
  const [selectedYear, setSelectedYear] = useState<string>(initialYear.toString());
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showOptionalNotes, setShowOptionalNotes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resourceBudgetChanges, setResourceBudgetChanges] = useState<any>(null);
  const [currentBudgetData, setCurrentBudgetData] = useState<BudgetData>(budgetData);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [catalogRoles, setCatalogRoles] = useState<RoleOption[]>([]);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

  // Revision panel state
  const [reasonCategory, setReasonCategory] = useState<RevisionReasonCategory | ''>('');
  const [revisionReason, setRevisionReason] = useState('');

  // Cascade confirm dialog state
  const [cascadeDialogOpen, setCascadeDialogOpen] = useState(false);
  const [pendingSaveAsDraft, setPendingSaveAsDraft] = useState(false);

  // Master role catalog — used by the import dialog to validate roles and source default rates
  useEffect(() => {
    let cancelled = false;
    fetchRoles()
      .then((roles) => {
        if (!cancelled) setCatalogRoles(roles);
      })
      .catch((err) => {
        console.error('[EditResourceBudgetPage] Failed to fetch roles:', err);
        if (!cancelled) setCatalogRoles([]);
      });
    return () => { cancelled = true; };
  }, []);

  // Hooks
  const { settings: budgetPermissions } = useBudgetPermissions();

  // Undo/Redo state
  const MAX_HISTORY = 30;
  const [history, setHistory] = useState<any[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [undoRedoVersion, setUndoRedoVersion] = useState(0); // Used to force form remount on undo/redo
  const isUndoRedoInProgress = useRef(false); // Flag to prevent history updates during undo/redo

  // Refs for stable access in the cell-commit handler
  const latestChangesRef = useRef<any>(null);
  const historyRef = useRef(history);
  const historyIndexRef = useRef(historyIndex);
  const hasUserInteractedRef = useRef(hasUserInteracted);
  const currentBudgetDataRef = useRef(currentBudgetData);

  historyRef.current = history;
  historyIndexRef.current = historyIndex;
  hasUserInteractedRef.current = hasUserInteracted;
  currentBudgetDataRef.current = currentBudgetData;

  // Stable handler: push to undo history when a cell edit is committed (blur)
  const handleCellCommit = useCallback(() => {
    setTimeout(() => {
      const changes = latestChangesRef.current;
      if (!changes) return;

      if (!hasUserInteractedRef.current) {
        hasUserInteractedRef.current = true;
        setHasUserInteracted(true);
        const initial = currentBudgetDataRef.current.resourceBudget;
        const newHistory = [initial, changes];
        historyRef.current = newHistory;
        historyIndexRef.current = 1;
        setHistory(newHistory);
        setHistoryIndex(1);
        setIsDirty(true);
        return;
      }

      setIsDirty(true);
      const idx = historyIndexRef.current;
      const newHistory = historyRef.current.slice(0, idx + 1);
      newHistory.push(changes);
      if (newHistory.length > MAX_HISTORY) newHistory.shift();
      historyRef.current = newHistory;
      historyIndexRef.current = newHistory.length - 1;
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }, 0);
  }, []);

  // Handle year change
  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    
    const yearNum = parseInt(year);
    const currentYearNum = new Date().getFullYear();
    
    // Create new budget data based on selected year
    const updatedBudgetData: BudgetData = {
      ...budgetData,
      resourceBudget: {
        ...budgetData.resourceBudget,
        byRole: budgetData.resourceBudget.byRole.map(role => {
          // If selecting current year (2026), use the original data
          if (yearNum === currentYearNum) {
            return role;
          }
          
          // If selecting a future year (2027+), show empty data
          if (yearNum > currentYearNum) {
            return {
              ...role,
              budgetHours: 0,
              budgetCost: 0,
              monthlyHours: {},
            };
          }
          
          // If selecting a past year (2025 or earlier), show empty data
          return {
            ...role,
            budgetHours: 0,
            budgetCost: 0,
            monthlyHours: {},
          };
        }),
      },
    };
    
    setCurrentBudgetData(updatedBudgetData);
    setIsDirty(false); // Reset dirty state when changing years
    setResourceBudgetChanges(null); // Clear any pending changes
    setHasUserInteracted(false); // Reset interaction flag when changing years
    
    // Reset undo/redo history when changing years
    setHistory([]);
    setHistoryIndex(-1);
    
    console.log(`[EditResourceBudgetPage] Year changed to: ${year}`);
  };

  // In edit mode: validate + open cascade confirm dialog (or save as draft directly)
  const handleSave = (asDraft = false) => {
    if (!isDirty) return;

    const requireNotes = budgetPermissions.revisionSettings?.requireRevisionNotes ?? false;

    // Only enforce category/notes when the setting is on
    if (requireNotes && !reasonCategory) {
      setError('Please select a revision category before saving.');
      return;
    }

    setError(null);
    setPendingSaveAsDraft(asDraft);

    if (asDraft) {
      // Draft saves skip cascade confirm — cascade only applies when revision becomes active
      handleCascadeConfirmDirect(asDraft);
    } else {
      setCascadeDialogOpen(true);
    }
  };

  // Internal helper for draft saves (bypasses cascade dialog)
  const handleCascadeConfirmDirect = async (asDraft: boolean) => {
    try {
      setIsSaving(true);
      setError(null);

      const updatedBudget: Partial<BudgetData> = {};
      if (resourceBudgetChanges) {
        updatedBudget.resourceBudget = resourceBudgetChanges;
      }

      const revisionData: Partial<BudgetRevision> = {
        reasonCategory: reasonCategory as RevisionReasonCategory,
        reason: revisionReason,
        status: asDraft ? 'draft' : 'active',
        draftType: null,
      };

      await onSave(updatedBudget, revisionData);

      toast.success(asDraft ? 'Draft saved' : 'Budget revision saved', {
        description: asDraft
          ? 'This revision is saved as a draft and is not yet active.'
          : reasonCategory
          ? `Saved as "${budgetPermissions.revisionSettings?.revisionReasonCategories?.find(c => c.id === reasonCategory)?.label ?? reasonCategory}"`
          : undefined,
      });

      setIsDirty(false);
      setResourceBudgetChanges(null);
      setReasonCategory('');
      setRevisionReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  // Called when user confirms in cascade dialog
  const handleCascadeConfirm = async (cascadeRecord: RevisionCascadeRecord) => {
    try {
      setIsSaving(true);
      setCascadeDialogOpen(false);
      setError(null);

      const updatedBudget: Partial<BudgetData> = {};
      if (resourceBudgetChanges) {
        updatedBudget.resourceBudget = resourceBudgetChanges;
      }

      const revisionData: Partial<BudgetRevision> = {
        reasonCategory: reasonCategory as RevisionReasonCategory,
        reason: revisionReason,
        status: 'active',
        cascadeRecord,
        draftType: null,
      };

      await onSave(updatedBudget, revisionData);

      toast.success('Budget revision saved', {
        description: reasonCategory
          ? `Saved as "${budgetPermissions.revisionSettings?.revisionReasonCategories?.find(c => c.id === reasonCategory)?.label ?? reasonCategory}"`
          : undefined,
      });

      // Reset state
      setIsDirty(false);
      setResourceBudgetChanges(null);
      setReasonCategory('');
      setRevisionReason('');
      setPendingSaveAsDraft(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget revision');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
    } else {
      onCancel();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleConfirmLeave = () => {
    setShowUnsavedChangesDialog(false);
    onCancel();
  };

  // Undo handler
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      // Set flag to prevent onChange from updating history during undo
      isUndoRedoInProgress.current = true;
      
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      setHistoryIndex(newIndex);
      setResourceBudgetChanges(previousState);
      // Update currentBudgetData so the form reflects the undone state
      setCurrentBudgetData(prev => ({
        ...prev,
        resourceBudget: previousState,
      }));
      setIsDirty(newIndex > 0); // Only dirty if not back to initial state
      // Increment version to force form remount with new state
      setUndoRedoVersion(v => v + 1);
      
      // Reset flag after a short delay to allow form to remount
      setTimeout(() => {
        isUndoRedoInProgress.current = false;
      }, 100);
    }
  }, [historyIndex, history]);

  // Redo handler
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      // Set flag to prevent onChange from updating history during redo
      isUndoRedoInProgress.current = true;
      
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      setHistoryIndex(newIndex);
      setResourceBudgetChanges(nextState);
      // Update currentBudgetData so the form reflects the redone state
      setCurrentBudgetData(prev => ({
        ...prev,
        resourceBudget: nextState,
      }));
      setIsDirty(true);
      // Increment version to force form remount with new state
      setUndoRedoVersion(v => v + 1);
      
      // Reset flag after a short delay to allow form to remount
      setTimeout(() => {
        isUndoRedoInProgress.current = false;
      }, 100);
    }
  }, [historyIndex, history]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.shiftKey && e.key === 'z' || e.key === 'y')) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);


  const handleImport = async (importData: ImportPreview, replaceMode: boolean, costCentreCode?: string | null) => {
    try {
      // Switch to the import year if different
      if (importData.year.toString() !== selectedYear) {
        setSelectedYear(importData.year.toString());
      }

      // Cost Centre mode: each imported role is stamped with a cost centre so it renders
      // under a cost centre in the grid and persists on save. In Cost Centre mode the
      // "Cost Centre" column is required, so each row carries its own code; the fallback
      // remains only for defensive/back-compat callers.
      const fallbackCcCode = costCentreCode ?? null;
      const roleCcCode = (importRole: ImportPreview['roles'][number]): string | null =>
        importRole.costCentreCode ?? fallbackCcCode;

      // Create updated budget data
      const updatedBudgetData = { ...currentBudgetData };
      
      if (replaceMode) {
        // Replace mode: in Cost Centre mode only replace roles in the cost centres being
        // imported (preserving other cost centres); otherwise replace all roles for the year.
        const importedCcCodes = new Set(
          importData.roles.map(r => roleCcCode(r)).filter((c): c is string => c !== null)
        );
        const preservedRoles = importedCcCodes.size > 0
          ? updatedBudgetData.resourceBudget.byRole.filter(
              r => !r.costCentreCode || !importedCcCodes.has(r.costCentreCode)
            )
          : [];
        updatedBudgetData.resourceBudget = {
          ...updatedBudgetData.resourceBudget,
          byRole: [
            ...preservedRoles,
            ...importData.roles.map(importRole => {
            const ccCode = roleCcCode(importRole);
            // Find existing role to get the rate
            const existingRole = budgetData.resourceBudget.byRole.find(
              r => r.role.toLowerCase() === importRole.role.toLowerCase()
                && (!ccCode || r.costCentreCode === ccCode)
            );
            
            const totalHours = importRole.totalHours;
            // Prefer the existing budget's rate, then the catalog default, then a flat fallback
            const billRate = existingRole?.billRate ?? importRole.defaultBillRate ?? 150;
            const costRate = existingRole?.costRate ?? importRole.defaultCostRate;
            const rateForCost = costRate ?? billRate;
            const actualHours = existingRole?.actualHours || 0;
            
            return {
              id: existingRole?.id || -Date.now(),
              role: importRole.role,
              budgetHours: totalHours,
              allocatedHours: existingRole?.allocatedHours || 0,
              actualHours,
              remainingHours: totalHours - actualHours,
              percentUsed: totalHours > 0 ? (actualHours / totalHours) * 100 : 0,
              billRate,
              costRate,
              budgetCost: totalHours * rateForCost,
              actualCost: actualHours * rateForCost,
              monthlyHours: importRole.monthlyHours,
              costCentreCode: ccCode,
            };
          }),
          ],
        };
      } else {
        // Additive mode: Update existing roles and add new ones
        const updatedRoles = [...updatedBudgetData.resourceBudget.byRole];
        
        importData.roles.forEach(importRole => {
          const ccCode = roleCcCode(importRole);
          const existingIndex = updatedRoles.findIndex(
            r => r.role.toLowerCase() === importRole.role.toLowerCase()
              && (!ccCode || r.costCentreCode === ccCode)
          );
          
          const totalHours = importRole.totalHours;
          
          if (existingIndex >= 0) {
            // Update existing role
            const existingRole = updatedRoles[existingIndex];
            const billRate = existingRole.billRate;
            
            updatedRoles[existingIndex] = {
              ...existingRole,
              budgetHours: totalHours,
              monthlyHours: importRole.monthlyHours,
              remainingHours: totalHours - existingRole.actualHours,
              percentUsed: totalHours > 0 ? (existingRole.actualHours / totalHours) * 100 : 0,
              budgetCost: totalHours * billRate,
            };
          } else {
            // Add new role — source rates from the master role catalog when available
            const billRate = importRole.defaultBillRate ?? 150;
            const costRate = importRole.defaultCostRate;
            const rateForCost = costRate ?? billRate;
            
            updatedRoles.push({
              id: -Date.now(),
              role: importRole.role,
              budgetHours: totalHours,
              allocatedHours: 0,
              actualHours: 0,
              remainingHours: totalHours,
              percentUsed: 0,
              billRate,
              costRate,
              budgetCost: totalHours * rateForCost,
              actualCost: 0,
              monthlyHours: importRole.monthlyHours,
              costCentreCode: ccCode,
            });
          }
        });
        
        updatedBudgetData.resourceBudget = {
          ...updatedBudgetData.resourceBudget,
          byRole: updatedRoles,
        };
      }

      // Update the current budget data and mark as dirty
      setCurrentBudgetData(updatedBudgetData);
      setResourceBudgetChanges(updatedBudgetData.resourceBudget);
      setHasUserInteracted(true); // Import counts as user interaction
      setIsDirty(true);
      // Force the grid to re-initialise from the freshly imported data. The form derives
      // its rows once on mount (incl. the Cost Centre-mode ccRoles), so without a remount
      // imported roles would not appear until the page is reopened.
      setUndoRedoVersion(v => v + 1);

      // Show success message
      const newCount = importData.roles.filter(r => r.isNew).length;
      const updateCount = importData.roles.filter(r => !r.isNew).length;
      
      let message = `Successfully imported budget for ${importData.year}. `;
      if (newCount > 0) message += `${newCount} new role${newCount !== 1 ? 's' : ''} added. `;
      if (updateCount > 0) message += `${updateCount} role${updateCount !== 1 ? 's' : ''} updated.`;
      
      toast.success('Import Successful', {
        description: message,
      });
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Import Failed', {
        description: error instanceof Error ? error.message : 'An error occurred during import',
      });
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col rounded-lg">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                disabled={isSaving}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  {loadedFromRevision ? (
                    <><History className="h-5 w-5 text-blue-500" /> Edit Resource Budget</>
                  ) : isNewRevision ? (
                    <><FilePlus2 className="h-5 w-5 text-emerald-600" /> New Resource Budget Revision</>
                  ) : (
                    <><FileEdit className="h-5 w-5" /> Edit Resource Budget</>
                  )}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {`Update budget values for ${currentBudgetData.projectName}`}
                </p>
              </div>
              
              {/* Compact Unsaved Changes Indicator - inline in header */}
              {isDirty && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-warning-50 border border-warning-200 rounded-md text-warning-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">Unsaved changes</span>
                </div>
              )}
            </div>
            
            {/* Import and Export Buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExportDialogOpen(true)}
                disabled={isSaving || !currentBudgetData.resourceBudget.byRole || currentBudgetData.resourceBudget.byRole.length === 0}
                title="Export budget"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImportDialogOpen(true)}
                disabled={isSaving}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mt-3">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </div>

      {/* Loaded from historical revision banner */}
      {loadedFromRevision && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2.5 flex items-center gap-2 text-blue-800 text-sm">
          <Info className="h-4 w-4 flex-shrink-0 text-blue-500" />
          <span>
            Loaded from <strong>RB-v{loadedFromRevision.revisionNumber}</strong>
            {loadedFromRevision.date ? ` — ${new Date(loadedFromRevision.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
            . You are editing a copy of this historical revision. Saving will create a new draft.
          </span>
        </div>
      )}

      {/* New Revision banner */}
      {isNewRevision && !loadedFromRevision && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2.5 flex items-center gap-2 text-emerald-800 text-sm">
          <FilePlus2 className="h-4 w-4 flex-shrink-0 text-emerald-600" />
          <span>
            <strong>Creating New Revision</strong> — You are working on a new resource budget revision. Your changes will be saved as a new draft revision.
          </span>
        </div>
      )}

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* Grid fills edge-to-edge (no horizontal padding) */}
        <EditResourceBudgetForm
          key={`${selectedYear}-${undoRedoVersion}`}
          projectId={projectId}
          resourceBudget={currentBudgetData.resourceBudget}
          selectedYear={selectedYear}
          projectStartDate={projectStartDate}
          projectEndDate={projectEndDate}
          isCostCentreMode={isCostCentreMode}
          costCentres={costCentres}
          hierarchicalCostCentres={hierarchicalCostCentres}
          isStructureView={isStructureView}
          onChange={(changes) => {
            setResourceBudgetChanges(changes);
            latestChangesRef.current = changes;
            if (isUndoRedoInProgress.current) return;
            setIsDirty(true);
          }}
          onCellCommit={handleCellCommit}
        />

        {/* Panels below the grid get their own padding */}
        <div className="px-4 pb-4 space-y-4 mt-4">
          {/* Revision panel — required when requireRevisionNotes is on, optional otherwise */}
          {isDirty && budgetPermissions.revisionSettings?.requireRevisionNotes && (
            <BudgetRevisionPanel
              changePercentage={0}
              requiresApproval={false}
              reasonCategory={reasonCategory}
              onReasonCategoryChange={setReasonCategory}
              revisionReason={revisionReason}
              onRevisionReasonChange={setRevisionReason}
              costBudgetChanges={null}
              salesBudgetChanges={null}
              resourceBudgetChanges={resourceBudgetChanges}
              originalBudget={budgetData}
            />
          )}

          {/* Optional notes when requireRevisionNotes is off */}
          {!budgetPermissions.revisionSettings?.requireRevisionNotes && (
            <div className="border rounded-lg p-4 bg-muted/30">
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 w-full text-left"
                onClick={() => setShowOptionalNotes(prev => !prev)}
              >
                <MessageSquare className="h-4 w-4" />
                {showOptionalNotes ? 'Hide notes' : 'Add notes (optional)'}
              </button>
              {showOptionalNotes && (
                <div className="mt-3 space-y-3">
                  <BudgetRevisionPanel
                    changePercentage={0}
                    requiresApproval={false}
                    reasonCategory={reasonCategory}
                    onReasonCategoryChange={setReasonCategory}
                    revisionReason={revisionReason}
                    onRevisionReasonChange={setRevisionReason}
                    costBudgetChanges={null}
                    salesBudgetChanges={null}
                    resourceBudgetChanges={resourceBudgetChanges}
                    originalBudget={budgetData}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 z-10 bg-background border-t shadow-lg">
        <div className="px-4 py-3">
          <div className="flex justify-end items-center gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              size="lg"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            
            <Button
              variant="outline"
              onClick={handleUndo}
              disabled={historyIndex <= 0 || isSaving}
              size="lg"
              title="Undo (Ctrl+Z)"
            >
              <Undo className="h-4 w-4 mr-2" />
              Undo {historyIndex > 0 && `(${historyIndex})`}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1 || isSaving}
              size="lg"
              title="Redo (Ctrl+Shift+Z)"
            >
              <Redo className="h-4 w-4 mr-2" />
              Redo {historyIndex < history.length - 1 && `(${history.length - 1 - historyIndex})`}
            </Button>
            
            {loadedFromRevision || onMakeActive ? (
              <>
                {/* Draft / historical revision mode — Save as Draft + Make Active */}
                <Button
                  variant="outline"
                  onClick={() => handleSave(true)}
                  disabled={!isDirty || isSaving}
                  size="lg"
                >
                  <FileEdit className="h-4 w-4 mr-2" />
                  {isSaving && pendingSaveAsDraft ? 'Saving…' : 'Save as Draft'}
                </Button>

                <Button
                  onClick={onMakeActive}
                  disabled={isSaving}
                  size="lg"
                  className="bg-success-600 hover:bg-success-700 text-white"
                >
                  Make Active
                </Button>
              </>
            ) : (
              /* Normal edit of active revision — single Save button */
              <Button
                onClick={() => handleSave(false)}
                disabled={!isDirty || isSaving}
                size="lg"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving && !pendingSaveAsDraft ? 'Saving…' : 'Save'}
              </Button>
            )}
          </div>
        </div>
      </div>


      {/* Cascade Confirm Dialog */}
      <RevisionCascadeConfirmDialog
        open={cascadeDialogOpen}
        revision={{
          reasonCategory: reasonCategory as RevisionReasonCategory | undefined,
          reasonCategoryLabel: budgetPermissions.revisionSettings?.revisionReasonCategories?.find(c => c.id === reasonCategory)?.label,
          reason: revisionReason,
        }}
        impact={null}
        cascadeSettings={budgetPermissions.revisionSettings}
        isSaving={isSaving}
        projectType={budgetData.salesBudget?.projectType}
        isCostCentreMode={isCostCentreMode}
        onConfirm={handleCascadeConfirm}
        onCancel={() => setCascadeDialogOpen(false)}
      />

      {/* Import Dialog */}
      <ImportBudgetDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImport={handleImport}
        existingRoles={currentBudgetData.resourceBudget.byRole}
        catalogRoles={catalogRoles}
        currentYear={parseInt(selectedYear)}
        projectStartDate={projectStartDate}
        projectEndDate={projectEndDate}
        isCostCentreMode={isCostCentreMode}
        costCentres={costCentres}
      />

      {/* Export Dialog */}
      <BudgetExportModal
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        budgetData={currentBudgetData}
        projectStartDate={projectStartDate}
        projectEndDate={projectEndDate}
        resourceOnly={true}
      />

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure that you want to leave the current page? The changes that you made won't be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLeave}>Leave</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
